import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ImagePlus, Loader2, Save, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  DATE_EXTENSION_TYPE,
  DISCOUNT_TYPE,
  RECURRENCE,
} from './membership-enums';
import {
  useBookingLocations,
  useDeleteMembershipPlan,
  useSaveMembershipPlan,
  useUploadImage,
} from './memberships.api';
import type { MembershipPlan } from './types';

/**
 * Create or edit a membership plan.
 *
 * Every field of the original drawer, in the original order. The conditional
 * sections (promo, limit, auto-renewal discount) reveal on their switch, as
 * before — the difference is that the switch and the fields it controls are
 * visibly grouped rather than being siblings in one flat column.
 */

/** The blank plan the original's "create new" started from. */
const EMPTY_PLAN: Draft = {
  imageUrl: '',
  descriptionEn: '',
  descriptionEs: '',
  internalName: '',
  nameEs: '',
  nameEn: '',
  recurrence: 0,
  ordering: 0,
  price: 0,
  includedMembers: 0,
  locationId: null,
  isActive: false,
  promoPrice: null,
  promoStartDate: null,
  promoEndDate: null,
  dateExtensionType: 0,
  dateExtensionValue: 1,
  hasPromo: false,
  allowRenewal: true,
  hasLimit: false,
  limit: 1,
  autoRenewalDiscountValue: 0,
  autoRenewalDiscountType: 0,
  allowPayPalSubscription: false,
  hasAutoRenewalDiscount: false,
};

/** The form's own shape: the plan plus the two derived booleans. */
type Draft = Partial<MembershipPlan> & {
  hasPromo: boolean;
  hasLimit: boolean;
};

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <Label className="text-xs font-semibold">{label}</Label>
      {children}
    </div>
  );
}

function ToggleRow({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-1">
      <Label className="text-xs font-semibold">{label}</Label>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

/**
 * Limit and date extension are counts that start at 1. A cleared or typed-down
 * field would otherwise save 0 or less; snap it back when the field is left.
 */
function atLeastOne(value: number | null | undefined) {
  return Math.max(1, Math.round(value ?? 1) || 1);
}

/** `<input type="date">` wants `yyyy-MM-dd`; the API sends ISO timestamps. */
function toDateInput(value: string | null | undefined) {
  if (!value) return '';
  const date = new Date(/[zZ]$/.test(value) ? value : `${value}Z`);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
}

export function EditPlanSheet({
  plan,
  open,
  onOpenChange,
}: {
  /** `null` opens the sheet on a blank plan. */
  plan: MembershipPlan | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const fileInput = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState<Draft>(EMPTY_PLAN);

  const { data: locations, isPending: loadingLocations } =
    useBookingLocations(open);
  const save = useSaveMembershipPlan();
  const remove = useDeleteMembershipPlan();
  const upload = useUploadImage('subscriptions');

  useEffect(() => {
    if (!open) return;
    setDraft(
      plan
        ? {
            ...plan,
            // Both derived, exactly as the original derived them.
            hasPromo: Boolean(plan.promoStartDate && plan.promoEndDate),
            hasLimit: (plan.limit ?? 0) > 0,
            // A percentage is stored as a fraction and shown as a whole number.
            autoRenewalDiscountValue:
              plan.autoRenewalDiscountType === 1
                ? (plan.autoRenewalDiscountValue ?? 0) * 100
                : (plan.autoRenewalDiscountValue ?? 0),
          }
        : EMPTY_PLAN
    );
  }, [plan, open]);

  const set = (patch: Partial<Draft>) =>
    setDraft((current) => ({ ...current, ...patch }));

  const isEditing = Boolean(plan?.id);

  function submit() {
    // The same normalisation the original applied before sending.
    const payload: Partial<MembershipPlan> & { id?: string } = {
      ...draft,
      limit: atLeastOne(draft.limit),
      dateExtensionValue: atLeastOne(draft.dateExtensionValue),
    };
    delete (payload as Record<string, unknown>).hasPromo;
    delete (payload as Record<string, unknown>).hasLimit;

    if (!draft.hasPromo) {
      payload.promoStartDate = null;
      payload.promoEndDate = null;
      payload.promoPrice = null;
      payload.dateExtensionType = 0;
      payload.dateExtensionValue = 1;
    }
    if (draft.autoRenewalDiscountType === 1) {
      payload.autoRenewalDiscountValue =
        (draft.autoRenewalDiscountValue ?? 0) / 100;
    }
    if (!draft.hasLimit) {
      payload.limit = null;
    }

    save.mutate(payload, {
      onSuccess: () => {
        toast.success(t('memberships.planSaved'));
        onOpenChange(false);
      },
      onError: (error) =>
        toast.error(
          error instanceof Error ? error.message : t('memberships.saveFailed')
        ),
    });
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-0 sm:max-w-lg"
      >
        <SheetHeader className="border-b">
          <SheetTitle>
            {isEditing
              ? t('memberships.editPlan')
              : t('memberships.createPlan')}
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-5 p-4">
          {isEditing && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="destructive"
                  className="w-full"
                  disabled={remove.isPending}
                >
                  {remove.isPending ? (
                    <Loader2 className="animate-spin" />
                  ) : (
                    <Trash2 />
                  )}
                  {t('memberships.deletePlan')}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {t('memberships.deletePlanConfirmTitle')}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {t('memberships.deletePlanConfirmBody')}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={() =>
                      remove.mutate(plan!.id, {
                        onSuccess: () => {
                          toast.success(t('memberships.planDeleted'));
                          onOpenChange(false);
                        },
                        onError: () =>
                          toast.error(t('memberships.deleteFailed')),
                      })
                    }
                  >
                    {t('memberships.deletePlan')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}

          <ToggleRow
            label={t('memberships.isActive')}
            checked={Boolean(draft.isActive)}
            onChange={(isActive) => set({ isActive })}
          />

          <Field label={t('memberships.ordering')}>
            <Input
              type="number"
              value={draft.ordering ?? 0}
              onChange={(e) => set({ ordering: Number(e.target.value) })}
            />
          </Field>

          <Field label={t('memberships.internalName')}>
            <Input
              value={draft.internalName ?? ''}
              onChange={(e) => set({ internalName: e.target.value })}
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={`${t('memberships.name')} (EN)`}>
              <Input
                value={draft.nameEn ?? ''}
                onChange={(e) => set({ nameEn: e.target.value })}
              />
            </Field>
            <Field label={`${t('memberships.name')} (ES)`}>
              <Input
                value={draft.nameEs ?? ''}
                onChange={(e) => set({ nameEs: e.target.value })}
              />
            </Field>
          </div>

          <Field label={`${t('memberships.description')} (EN)`}>
            <Textarea
              rows={3}
              value={draft.descriptionEn ?? ''}
              onChange={(e) => set({ descriptionEn: e.target.value })}
            />
          </Field>
          <Field label={`${t('memberships.description')} (ES)`}>
            <Textarea
              rows={3}
              value={draft.descriptionEs ?? ''}
              onChange={(e) => set({ descriptionEs: e.target.value })}
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('memberships.includedMembers')}>
              <Input
                type="number"
                min={0}
                value={draft.includedMembers ?? 0}
                onChange={(e) =>
                  set({ includedMembers: Number(e.target.value) })
                }
              />
            </Field>
            <Field label={t('memberships.price')}>
              <Input
                type="number"
                min={0}
                step="0.01"
                value={draft.price ?? 0}
                onChange={(e) => set({ price: Number(e.target.value) })}
              />
            </Field>
          </div>

          <Field label={t('memberships.recurrence')}>
            <Select
              value={String(draft.recurrence ?? 0)}
              onValueChange={(v) => set({ recurrence: Number(v) })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RECURRENCE.map((option) => (
                  <SelectItem key={option.value} value={String(option.value)}>
                    {t(option.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <ToggleRow
            label={t('memberships.allowRenewal')}
            checked={Boolean(draft.allowRenewal)}
            onChange={(allowRenewal) => set({ allowRenewal })}
          />
          <ToggleRow
            label={t('memberships.allowPaypalSubscription')}
            checked={Boolean(draft.allowPayPalSubscription)}
            onChange={(allowPayPalSubscription) =>
              set({ allowPayPalSubscription })
            }
          />

          <div className="space-y-3 rounded-lg border p-3">
            <ToggleRow
              label={t('memberships.hasLimit')}
              checked={draft.hasLimit}
              onChange={(hasLimit) => set({ hasLimit })}
            />
            {draft.hasLimit && (
              <Field label={t('memberships.limit')}>
                <Input
                  type="number"
                  min={1}
                  value={draft.limit ?? 1}
                  onChange={(e) => set({ limit: Number(e.target.value) })}
                  onBlur={() => set({ limit: atLeastOne(draft.limit) })}
                />
              </Field>
            )}
          </div>

          <div className="space-y-3 rounded-lg border p-3">
            <ToggleRow
              label={t('memberships.hasPromo')}
              checked={draft.hasPromo}
              onChange={(hasPromo) => set({ hasPromo })}
            />
            {draft.hasPromo && (
              <>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label={t('memberships.promoStart')}>
                    <Input
                      type="date"
                      value={toDateInput(draft.promoStartDate)}
                      onChange={(e) =>
                        set({ promoStartDate: e.target.value || null })
                      }
                    />
                  </Field>
                  <Field label={t('memberships.promoEnd')}>
                    <Input
                      type="date"
                      value={toDateInput(draft.promoEndDate)}
                      onChange={(e) =>
                        set({ promoEndDate: e.target.value || null })
                      }
                    />
                  </Field>
                </div>
                <Field label={t('memberships.promoPrice')}>
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    value={draft.promoPrice ?? 0}
                    onChange={(e) => set({ promoPrice: Number(e.target.value) })}
                  />
                </Field>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label={t('memberships.dateExtensionType')}>
                    <Select
                      value={String(draft.dateExtensionType ?? 0)}
                      onValueChange={(v) =>
                        set({ dateExtensionType: Number(v) })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DATE_EXTENSION_TYPE.map((option) => (
                          <SelectItem
                            key={option.value}
                            value={String(option.value)}
                          >
                            {t(option.labelKey)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label={t('memberships.dateExtension')}>
                    <Input
                      type="number"
                      min={1}
                      value={draft.dateExtensionValue ?? 1}
                      onChange={(e) =>
                        set({ dateExtensionValue: Number(e.target.value) })
                      }
                      onBlur={() =>
                        set({
                          dateExtensionValue: atLeastOne(
                            draft.dateExtensionValue
                          ),
                        })
                      }
                    />
                  </Field>
                </div>
              </>
            )}
          </div>

          <div className="space-y-3 rounded-lg border p-3">
            <ToggleRow
              label={t('memberships.hasAutoRenewalDiscount')}
              checked={Boolean(draft.hasAutoRenewalDiscount)}
              onChange={(hasAutoRenewalDiscount) =>
                set({ hasAutoRenewalDiscount })
              }
            />
            {draft.hasAutoRenewalDiscount && (
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label={t('memberships.autoRenewalDiscountType')}>
                  <Select
                    value={String(draft.autoRenewalDiscountType ?? 0)}
                    onValueChange={(v) =>
                      set({ autoRenewalDiscountType: Number(v) })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DISCOUNT_TYPE.map((option) => (
                        <SelectItem
                          key={option.value}
                          value={String(option.value)}
                        >
                          {t(option.labelKey)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label={t('memberships.autoRenewalDiscountValue')}>
                  <Input
                    type="number"
                    min={0}
                    value={draft.autoRenewalDiscountValue ?? 0}
                    onChange={(e) =>
                      set({ autoRenewalDiscountValue: Number(e.target.value) })
                    }
                  />
                </Field>
              </div>
            )}
          </div>

          <Field label={t('memberships.location')}>
            {loadingLocations ? (
              <Skeleton className="h-9 w-full" />
            ) : (
              <Select
                value={draft.locationId ?? 'ANY'}
                onValueChange={(v) => set({ locationId: v === 'ANY' ? null : v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ANY">{t('memberships.any')}</SelectItem>
                  {(locations ?? []).map((location) => (
                    <SelectItem key={location.id} value={location.id}>
                      {location.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>

          <Field label={t('memberships.image')}>
            <div className="space-y-2">
              {draft.imageUrl && (
                <img
                  src={draft.imageUrl}
                  alt=""
                  className="bg-muted max-h-40 w-full rounded-lg object-contain"
                />
              )}
              <input
                ref={fileInput}
                type="file"
                accept="image/*"
                hidden
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (!file) return;
                  upload.mutate(file, {
                    onSuccess: (url) => set({ imageUrl: url }),
                    onError: () => toast.error(t('memberships.uploadFailed')),
                  });
                  event.target.value = '';
                }}
              />
              <Button
                variant="outline"
                className="w-full border-dashed"
                disabled={upload.isPending}
                onClick={() => fileInput.current?.click()}
              >
                {upload.isPending ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  <ImagePlus />
                )}
                {t('memberships.clickOrDrag')}
              </Button>
            </div>
          </Field>

          <Button
            className="w-full"
            disabled={save.isPending}
            onClick={submit}
          >
            {save.isPending ? <Loader2 className="animate-spin" /> : <Save />}
            {isEditing ? t('memberships.update') : t('memberships.create')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
